import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, tokenStore, type User } from './api/client';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  // localStorage is not reactive. Mirror the access token in state so the `me`
  // query's `enabled` flag — and every consumer of this context — re-renders
  // the moment we log in or out. Without this the query keeps the `enabled:
  // false` it was created with, never fetches `/auth/me` after login, and the
  // protected route bounces straight back to /login.
  const [token, setToken] = useState<string | null>(() => tokenStore.access);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['me'],
    queryFn: api.me,
    enabled: Boolean(token),
    retry: false,
    staleTime: 60_000,
  });

  // A stored token the server rejects (expired, revoked, or invalidated by a
  // password reset) is cleared by the client on the failed refresh — drop it
  // from state too so we settle on /login instead of spinning.
  useEffect(() => {
    if (isError && !tokenStore.access) setToken(null);
  }, [isError]);

  const login = useCallback(
    async (email: string, password: string) => {
      await api.login(email, password);
      setToken(tokenStore.access);
      // Prime ['me'] before the caller navigates, so the protected route sees
      // a user on its first render instead of flashing back to /login.
      await queryClient.fetchQuery({ queryKey: ['me'], queryFn: api.me });
    },
    [queryClient],
  );

  const logout = useCallback(() => {
    api.logout();
    setToken(null);
    queryClient.clear();
  }, [queryClient]);

  const value = useMemo<AuthState>(
    () => ({
      user: data ?? null,
      isLoading: Boolean(token) && isLoading,
      login,
      logout,
    }),
    [data, token, isLoading, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider');
  return context;
}
